import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, Spinner, Alert, Chip, EmptyState } from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import { NEUTRAL_BTN } from '../lib/buttonStyles';
import {
  adminApiClient,
  type ProductImageQueueItem,
  type ProductImageQueueState,
  type ProductImageQueueStats,
} from '../lib/adminApiClient';

/**
 * Product-image sourcing queue.
 *
 * Rod, 2026-08-07: *"do the free pass but I need to be able to pull these up in
 * admin somehow so I can hire someone to get the information we need for
 * images."* This is that screen — the human half of the image strategy, and the
 * prerequisite for switching `productImageFind` back on, since that job routes
 * everything it cannot resolve to a bin that did not exist until now.
 *
 * 🛑 Deliberately NO image preview. A contractor pastes a URL we have not
 * fetched yet, and rendering it here would put a third-party image on our own
 * screen — the exact thing "we serve only our own hosted images" forbids, and
 * it would also leak our admins' IPs to whatever host they pasted. Confirmation
 * comes from the row leaving the queue once the rehost sweep hosts our copy.
 */

const STATES: { key: ProductImageQueueState; label: string; hint: string }[] = [
  { key: 'not_found', label: 'Finder gave up', hint: 'The automated finder tried and failed — a human is the next step.' },
  { key: 'unattempted', label: 'Never attempted', hint: 'No automated attempt yet. The bulk of the catalogue sits here.' },
  { key: 'all', label: 'All', hint: 'Missing-image products, including products awaiting evidence review; most-attempted first.' },
];

export default function ProductImageQueue() {
  const caps = useCapabilities();
  const canSubmit = caps.has('productimage:submit');
  const canRead=caps.has('productimage:read');
  const request=useRef(0);

  const [state, setState] = useState<ProductImageQueueState>('not_found');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<ProductImageQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<ProductImageQueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const load = useCallback(async () => {
    const sequence=++request.current;
    setLoading(true);
    setError(null);
    try {
      const [res, st] = await Promise.all([
        adminApiClient.listProductImageQueue(state, q.trim() || undefined),
        adminApiClient.productImageQueueStats(),
      ]);
      if(sequence!==request.current)return;
      setItems(res.items);
      setTotal(res.total);
      setStats(st);
    } catch (err) {
      if(sequence===request.current)setError(err instanceof Error ? err.message : 'Failed to load the queue');
    } finally {
      if(sequence===request.current)setLoading(false);
    }
  }, [state, q]);

  // Deliberately keyed on state/access, not on `load`. `load` also closes over
  // the search box, so depending on it would refetch on every keystroke; the
  // Search button is the explicit trigger for that. Switching tabs still picks
  // up whatever is currently typed, because `load` is rebuilt each render.
  useEffect(() => {
    if(canRead)void load();
    return()=>{request.current++;};
  }, [state,canRead]);

  const done = (kind: 'success' | 'error', msg: string) => {
    setFlash({ kind, msg });
    void load();
  };

  if(caps.loading)return <Spinner/>;
  if(!canRead)return <Alert severity="error">You don’t have permission to view image sourcing.</Alert>;

  return (
    <Box sx={{ p: 3, maxWidth: 1100 }}>
      <Typography variant="h1" sx={{ fontSize: 26, fontWeight: 700, mb: 0.5 }}>
        Product images
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#5a7063', mb: 2 }}>
        Find a photo for each product and paste its URL. We download it and host our own
        copy — the pasted link is never shown to anyone.
      </Typography>

      {stats && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${stats.notFound} finder gave up`} />
          <Chip label={`${stats.unattempted} never attempted`} />
          <Chip label={`${stats.pendingRehost} awaiting rehost`} />
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {STATES.map((s) => (
          <Button
            key={s.key}
            variant={state === s.key ? 'contained' : 'outlined'}
            buttonText={s.label}
            ariaLabel={s.label}
            onClick={() => setState(s.key)}
            sx={state === s.key ? undefined : NEUTRAL_BTN}
          />
        ))}
      </Box>
      <Typography sx={{ fontSize: 12, color: '#5a7063', mb: 2 }}>
        {STATES.find((s) => s.key === state)?.hint}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Search name, brand or UPC"
          ariaLabel="Search name, brand or UPC"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          size="small"
        />
        <Button variant="outlined" buttonText="Search" ariaLabel="Search" onClick={() => void load()} sx={NEUTRAL_BTN} />
      </Box>

      {flash && (
        <Alert severity={flash.kind === 'success' ? 'success' : 'error'} sx={{ mb: 2 }}>
          {flash.msg}
        </Alert>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading&&items.length===0 ? (
        <Spinner />
      ) : error&&items.length===0?null:items.length === 0 ? (
        <EmptyState title="Nothing in this queue" />
      ) : (
        <>
          <Typography sx={{ fontSize: 13, color: '#5a7063', mb: 1 }}>
            Showing {items.length} of {total}
          </Typography>
          {items.map((it) => (
            <QueueRow key={it.glpProductId} item={it} canSubmit={canSubmit&&!loading} onDone={done} />
          ))}
        </>
      )}
    </Box>
  );
}

function QueueRow({
  item,
  canSubmit,
  onDone,
}: {
  item: ProductImageQueueItem;
  canSubmit: boolean;
  onDone: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSkip, setShowSkip] = useState(false);
  const [expectedRevision,setExpectedRevision]=useState(item.evidenceRevision);
  const versionChanged=expectedRevision!==item.evidenceRevision;

  const submit = async () => {
    setBusy(true);
    try {
      const reply=await adminApiClient.submitProductImageSource(item.glpProductId, url.trim(), note.trim() || undefined,expectedRevision);
      if(item.evidenceRevision&&reply.revision)setExpectedRevision(reply.revision);
      onDone('success', item.evidenceRevision?`Image suggestion saved for ${item.itemName}. Verification and hosting are still required.`:`Queued ${item.itemName}. The rehost sweep will host our copy.`);
    } catch (err) {
      onDone('error', err instanceof Error ? err.message : 'Could not save that URL');
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setBusy(true);
    try {
      await adminApiClient.skipProductImage(item.glpProductId, reason.trim());
      onDone('success', `Marked "${item.itemName}" as having no findable image.`);
    } catch (err) {
      onDone('error', err instanceof Error ? err.message : 'Could not skip that product');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{item.itemName}</Typography>
          <Typography sx={{ fontSize: 13, color: '#5a7063' }}>
            {[item.brand, item.glpDepartment].filter(Boolean).join(' · ')}
          </Typography>
          {/* The UPC is the contractor's search key — a barcode search finds the
              exact pack size, where the name alone often does not. */}
          <Typography sx={{ fontSize: 13, fontFamily: 'monospace', mt: 0.5 }}>{item.upc12}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {item.pendingRehost && <Chip label="Awaiting rehost" />}
          {item.pendingEvidenceReview && <Chip label="Image suggestion awaiting validation" />}
          {item.imageFindState === 'not_found' && <Chip label={`Finder failed ×${item.imageFindAttempts ?? 0}`} />}
        </Box>
      </Box>

      {versionChanged&&canSubmit&&<Box sx={{mt:1}}><Alert severity="warning">This product changed. Your URL and notes are preserved; review the current product before submitting.</Alert><Button ariaLabel={`Use current version of ${item.itemName}`} buttonText="Use current product version" variant="outlined" onClick={()=>setExpectedRevision(item.evidenceRevision)} disabled={busy}/></Box>}
      {canSubmit && !item.pendingRehost && !item.pendingEvidenceReview && (
        <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Image URL (https)"
            ariaLabel={`Image URL for ${item.itemName}`}
            placeholder="https://…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            size="small"
            sx={{ minWidth: 340 }}
          />
          <TextField
            label="Where from (optional)"
            ariaLabel={`Source note for ${item.itemName}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
          />
          <Button
            variant="contained"
            buttonText="Save"
            ariaLabel={`Save image URL for ${item.itemName}`}
            onClick={() => void submit()}
            disabled={busy || versionChanged || !url.trim().startsWith('https://')}
          />
          {!item.evidenceRevision&&<Button
            variant="text"
            buttonText="No image exists"
            ariaLabel={`Mark ${item.itemName} as having no findable image`}
            onClick={() => setShowSkip((v) => !v)}
            disabled={busy}
          />}
        </Box>
      )}

      {showSkip && canSubmit && !item.evidenceRevision && (
        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Why is there no image?"
            ariaLabel={`Reason for ${item.itemName}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            sx={{ minWidth: 320 }}
          />
          <Button
            variant="outlined"
            buttonText="Confirm"
            ariaLabel="Confirm no image exists"
            onClick={() => void skip()}
            disabled={busy || reason.trim().length < 3}
            sx={NEUTRAL_BTN}
          />
        </Box>
      )}
    </Paper>
  );
}
